import { useCallback, useEffect, useRef } from "react";
import { useAppDispatch } from "../../../app/hooks";
import { setIsTimeoutModalOpen } from "../LeaveInformationSlice";

interface UseInactivityTimerProps {
    isTimeoutModalOpen: boolean;
    timeoutDuration?: number; // in milliseconds
}

export const useInactivityTimer = ({
    isTimeoutModalOpen,
    timeoutDuration = 30000,
}: UseInactivityTimerProps): void => {
    const dispatch = useAppDispatch();
    const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const resetInactivityTimer = useCallback(() => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }

        timeoutRef.current = setTimeout(() => {
            dispatch(setIsTimeoutModalOpen(true));
        }, timeoutDuration);
    }, [dispatch, timeoutDuration]);

    const handleUserActivity = useCallback(() => {
        if (!isTimeoutModalOpen) {
            resetInactivityTimer();
        }
    }, [resetInactivityTimer, isTimeoutModalOpen]);

    useEffect(() => {
        resetInactivityTimer();

        const events = [
            "mousedown",
            "mousemove",
            "keypress",
            "scroll",
            "touchstart",
            "click",
        ];

        events.forEach((event) => {
            document.addEventListener(event, handleUserActivity, true);
        });

        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
            events.forEach((event) => {
                document.removeEventListener(event, handleUserActivity, true);
            });
        };
    }, [resetInactivityTimer, handleUserActivity]);

    useEffect(() => {
        if (!isTimeoutModalOpen) {
            resetInactivityTimer();
        }
    }, [isTimeoutModalOpen, resetInactivityTimer]);
};
